import { onMounted, onUnmounted, ref } from 'vue'

/**
 * 键盘快捷键Hook
 */
export function useKeyboard(options = {}) {
  const {
    onKeydown,
    enabled = true,
    target = window
  } = options

  const activeShortcuts = ref(new Map())

  // 默认快捷键
  const defaultShortcuts = {
    'n': { label: '新建日程', action: 'create' },
    't': { label: '跳转到今天', action: 'today' },
    'ArrowLeft': { label: '上一天/周/月', action: 'prev' },
    'ArrowRight': { label: '下一天/周/月', action: 'next' },
    'ArrowUp': { label: '上一周', action: 'prevWeek' },
    'ArrowDown': { label: '下一周', action: 'nextWeek' },
    'm': { label: '月视图', action: 'monthView' },
    'w': { label: '周视图', action: 'weekView' },
    'd': { label: '日视图', action: 'dayView' },
    'a': { label: '议程视图', action: 'agendaView' },
    'Delete': { label: '删除选中', action: 'delete' },
    'Escape': { label: '关闭弹窗', action: 'close' },
    '/': { label: '搜索', action: 'search' }
  }

  // 格式化修饰键
  function formatModifiers(modifiers) {
    const parts = []
    if (modifiers.ctrl) parts.push('Ctrl')
    if (modifiers.alt) parts.push('Alt')
    if (modifiers.shift) parts.push('Shift')
    if (modifiers.meta) parts.push('Meta')
    return parts.join('+')
  }

  // 解析快捷键
  function parseShortcut(key) {
    const parts = key.toLowerCase().split('+')
    const modifiers = {
      ctrl: false,
      alt: false,
      shift: false,
      meta: false
    }

    const keys = parts.filter(part => {
      if (part === 'ctrl') { modifiers.ctrl = true; return false }
      if (part === 'alt') { modifiers.alt = true; return false }
      if (part === 'shift') { modifiers.shift = true; return false }
      if (part === 'meta') { modifiers.meta = true; return false }
      return true
    })

    return {
      key: keys[0] || '',
      modifiers
    }
  }

  // 检查修饰键
  function checkModifiers(event, modifiers) {
    if (modifiers.ctrl && !event.ctrlKey) return false
    if (modifiers.alt && !event.altKey) return false
    if (modifiers.shift && !event.shiftKey) return false
    if (modifiers.meta && !event.metaKey) return false
    return true
  }

  // 绑定快捷键
  function bind(key, action, customLabel) {
    const shortcut = parseShortcut(key)
    activeShortcuts.value.set(key, {
      ...shortcut,
      action,
      label: customLabel || defaultShortcuts[key]?.label || key
    })
  }

  // 解绑快捷键
  function unbind(key) {
    activeShortcuts.value.delete(key)
  }

  // 处理键盘事件
  function handleKeydown(e) {
    if (!enabled) return

    const key = e.key.toLowerCase()

    for (const [, shortcut] of activeShortcuts.value) {
      const { key: shortcutKeyKey, modifiers } = shortcut

      if (key === shortcutKeyKey && checkModifiers(e, modifiers)) {
        e.preventDefault()
        e.stopPropagation()

        onKeydown?.(e, shortcut.action, shortcut)

        return
      }
    }
  }

  // 获取快捷键提示
  function getShortcutTips() {
    const tips = []
    for (const [, shortcut] of activeShortcuts.value) {
      const modifierStr = formatModifiers(shortcut.modifiers)
      const keyStr = shortcut.key.toUpperCase()
      tips.push({
        key: modifierStr ? `${modifierStr}+${keyStr}` : keyStr,
        label: shortcut.label,
        action: shortcut.action
      })
    }
    return tips
  }

  // 初始化默认快捷键
  function initDefaultShortcuts(actions) {
    const actionMap = {
      create: actions.createEvent || (() => {}),
      today: actions.goToToday || (() => {}),
      prev: actions.navigatePrev || (() => {}),
      next: actions.navigateNext || (() => {}),
      prevWeek: actions.navigatePrevWeek || (() => {}),
      nextWeek: actions.navigateNextWeek || (() => {}),
      monthView: actions.switchMonthView || (() => {}),
      weekView: actions.switchWeekView || (() => {}),
      dayView: actions.switchDayView || (() => {}),
      agendaView: actions.switchAgendaView || (() => {}),
      delete: actions.deleteSelected || (() => {}),
      close: actions.closeModal || (() => {}),
      search: actions.openSearch || (() => {})
    }

    for (const [key, shortcut] of Object.entries(defaultShortcuts)) {
      bind(key, actionMap[shortcut.action])
    }
  }

  // 生命周期
  onMounted(() => {
    target.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    target.removeEventListener('keydown', handleKeydown)
  })

  return {
    bind,
    unbind,
    getShortcutTips,
    initDefaultShortcuts,
    handleKeydown
  }
}

/**
 * 焦点管理Hook
 */
export function useFocusTrap(options = {}) {
  const { containerRef } = options

  const focusableSelector = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',')

  function getFocusableElements() {
    if (!containerRef.value) return []
    return containerRef.value.querySelectorAll(focusableSelector)
  }

  function handleKeydown(e) {
    if (e.key !== 'Tab') return

    const focusableElements = getFocusableElements()
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault()
      lastElement.focus()
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault()
      firstElement.focus()
    }
  }

  function focusFirst() {
    const elements = getFocusableElements()
    if (elements.length > 0) {
      elements[0].focus()
    }
  }

  function focusLast() {
    const elements = getFocusableElements()
    if (elements.length > 0) {
      elements[elements.length - 1].focus()
    }
  }

  onMounted(() => {
    containerRef.value?.addEventListener('keydown', handleKeydown)
    focusFirst()
  })

  onUnmounted(() => {
    containerRef.value?.removeEventListener('keydown', handleKeydown)
  })

  return {
    focusFirst,
    focusLast
  }
}
